import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Globe2, Headphones, Clock } from "lucide-react";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { LeadForm } from "../components/LeadForm";
import { WHATSAPP_LINK, WEBSITE_LINK, translations, getInitialLang, saveLang } from "../i18n";

const copy = {
  en: {
    kicker: "Contact & Support",
    title: "WE'RE ONE MESSAGE AWAY",
    sub: "Real humans, 24x7. Bonus activation, deposits, withdrawals — everything happens on WhatsApp.",
    wa_t: "WhatsApp Support",
    wa_d: "Fastest way to reach us. ID creation, bonus claims and payouts — all handled in one chat.",
    wa_btn: "Chat on WhatsApp",
    web_t: "Official Website",
    web_d: "Play live matches, check odds and manage your account on cric1x.com.",
    web_btn: "Open Cric1x.com",
    hours_t: "Support Hours",
    hours_d: "24 hours, 7 days a week. Including match days and holidays.",
    form_kicker: "Or leave your number",
    form_title: "WE'LL CALL YOU",
    form_sub: "Drop your details and our team will reach out on WhatsApp within minutes.",
  },
  hi: {
    kicker: "संपर्क और सपोर्ट",
    title: "बस एक मैसेज दूर",
    sub: "असली लोग, 24x7। बोनस एक्टिवेशन, डिपॉज़िट, विदड्रॉल — सब कुछ WhatsApp पर।",
    wa_t: "WhatsApp सपोर्ट",
    wa_d: "हम तक पहुंचने का सबसे तेज़ तरीका। ID बनाना, बोनस क्लेम और पेआउट — सब एक ही चैट में।",
    wa_btn: "WhatsApp पर चैट करें",
    web_t: "ऑफिशियल वेबसाइट",
    web_d: "लाइव मैच खेलें, ऑड्स देखें और अपना अकाउंट cric1x.com पर मैनेज करें।",
    web_btn: "Cric1x.com खोलें",
    hours_t: "सपोर्ट टाइमिंग",
    hours_d: "24 घंटे, हफ्ते के 7 दिन। मैच के दिन और छुट्टियों में भी।",
    form_kicker: "या अपना नंबर छोड़ें",
    form_title: "हम आपको कॉल करेंगे",
    form_sub: "अपनी डिटेल भरें, हमारी टीम मिनटों में WhatsApp पर संपर्क करेगी।",
  },
};

export default function Contact() {
  const [lang, setLangState] = useState(getInitialLang);
  const setLang = (l) => {
    saveLang(l);
    setLangState(l);
  };
  const t = copy[lang];
  const tf = translations[lang];

  return (
    <div className="min-h-screen bg-obsidian font-body text-white">
      <div className="noise-overlay" />
      <SiteHeader lang={lang} setLang={setLang} />

      <main className="mx-auto max-w-7xl px-5 pb-24 pt-32" data-testid="contact-page">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-[12px] uppercase tracking-[0.35em] text-gold">{t.kicker}</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold uppercase leading-none tracking-tighter sm:text-5xl lg:text-6xl">
            {t.title}
          </h1>
          <p className="mt-5 max-w-xl text-white/60">{t.sub}</p>
        </motion.div>

        <div className="mt-14 grid gap-16 lg:grid-cols-2">
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="border border-wa/40 bg-wa/5 p-8"
              data-testid="contact-whatsapp-card"
            >
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-wa">
                <MessageCircle size={15} /> {t.wa_t}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-white/70">{t.wa_d}</p>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 bg-wa px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-wide text-white transition-transform hover:scale-[0.97]"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle size={16} /> {t.wa_btn}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="border border-white/10 bg-surface p-8"
              data-testid="contact-website-card"
            >
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-white/50">
                <Globe2 size={15} className="text-gold" /> {t.web_t}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-white/70">{t.web_d}</p>
              <a
                href={WEBSITE_LINK}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:border-gold hover:text-gold"
                data-testid="contact-website-btn"
              >
                {t.web_btn}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="flex items-start gap-4 border border-white/10 bg-surface p-8"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-gold/10">
                <Clock className="text-gold" size={20} />
              </div>
              <div>
                <p className="flex items-center gap-2 font-display text-lg font-semibold uppercase">
                  <Headphones size={16} className="text-gold" /> {t.hours_t}
                </p>
                <p className="mt-2 text-sm text-white/60">{t.hours_d}</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="border border-gold/20 bg-surface p-8 sm:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">{t.form_kicker}</p>
              <h3 className="mt-3 font-display text-4xl font-bold uppercase leading-none gold-text">{t.form_title}</h3>
              <p className="mt-3 text-sm text-white/60">{t.form_sub}</p>
              <div className="mt-9">
                <LeadForm t={tf} />
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}
